import { useWeb3React } from "@web3-react/core";
import { useCallback, useEffect, useState } from "react";
import {
  getPoolDepositedAmount,
  getPoolWithdrawnAmount,
  getExchangeRate,
  getUserPoolShares,
} from "../utils/api/lendingPoolApi";

const useUserPoolSupply = (lendingPoolAddress: string) => {
  const { account, chainId, library } = useWeb3React();

  const [depositedAmount, setDepositedAmount] = useState<number>(0);
  const [withdrawnAmount, setWithdrawnAmount] = useState<number>(0);
  const [estimatedExchangeRate, setEstimatedExchangeRate] = useState<number>(0);
  const [shares, setShares] = useState<number>(0);
  const [balance, setBalance] = useState<number>(0);
  const [earned, setEarned] = useState<number>(0);

  const getWithdrawnAmount = useCallback(async () => {
    if (account) {
      const amount = await getPoolWithdrawnAmount(
        account,
        lendingPoolAddress,
        library
      );
      setWithdrawnAmount(amount);
    } else {
      setWithdrawnAmount(0);
    }
  }, [account, lendingPoolAddress, library]);

  const getExchangeRates = useCallback(async () => {
    if (!chainId) return;
    const rate = await getExchangeRate(lendingPoolAddress, chainId);
    setEstimatedExchangeRate(rate);
  }, [chainId, lendingPoolAddress]);

  const getShares = useCallback(async () => {
    if (account) {
      const shares = await getUserPoolShares(
        account,
        lendingPoolAddress,
        library
      );
      setShares(+shares);
    } else {
      setShares(0);
    }
  }, [account, lendingPoolAddress, library]);

  const getDepositedAmount = useCallback(async () => {
    if (account) {
      const amount = await getPoolDepositedAmount(
        account,
        lendingPoolAddress,
        library
      );
      setDepositedAmount(amount);
    } else {
      setShares(0);
    }
  }, [account, lendingPoolAddress, library]);

  useEffect(() => {
    setBalance(shares * estimatedExchangeRate);
  }, [shares, estimatedExchangeRate]);

  useEffect(() => {
    if (!shares) {
      return;
    }
    setEarned(
      shares * estimatedExchangeRate - (depositedAmount - withdrawnAmount)
    );
  }, [shares, estimatedExchangeRate, depositedAmount, withdrawnAmount]);

  const refreshState = () => {
    setDepositedAmount(0);
    setWithdrawnAmount(0);
    setShares(0);
    setBalance(0);
    setEarned(0);
  };

  const updateUserSupply = useCallback(() => {
    refreshState();

    getDepositedAmount();
    getWithdrawnAmount();
    getExchangeRates();
    getShares();
  }, [getDepositedAmount, getWithdrawnAmount, getExchangeRates, getShares]);

  useEffect(() => {
    if (!lendingPoolAddress || !account || !chainId) return;
    updateUserSupply();
  }, [lendingPoolAddress, account, chainId, updateUserSupply]);

  return {
    depositedAmount,
    withdrawnAmount,
    estimatedExchangeRate,
    shares,
    balance,
    earned,
    updateUserSupply,
  };
};

export default useUserPoolSupply;
